import React, { useEffect, useMemo, useState } from 'react';
import { FiPlusCircle } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import PortalDataTable from "../../../components/PortalDataTable";
import { SearchBox } from "../../../components";
import { formColumns } from "../../../utils";
import {
  useGetFormTypesPaged,
  useSearchFormTypes
} from '../../../hooks/forms/useGet';
import NetworkError from '../../../components/NetworkError';

const Forms = () => {
  // parent-owned pagination (hooks first)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // base paged list
  const {
    data: basePage,
    isLoading: isBaseLoading,
    isError: isBaseError,
    error: baseError
  } = useGetFormTypesPaged(page, pageSize);

  // search box (debounced; min 3 chars)
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => {
      const q = query.trim();
      setDebouncedQuery(q.length >= 3 ? q : null);
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  // reset to first page when search text changes
  useEffect(() => {
    setPage(1);
  }, [debouncedQuery]);

  // effective search filters
  const effectiveFilters = useMemo(() => {
    if (!debouncedQuery) return null;
    return {
      RequestValue: debouncedQuery,   // adjust if your API expects a different name (e.g., "q")
      PageNumber: page,
      PageSize: pageSize,
    };
  }, [debouncedQuery, page, pageSize]);

  // server-side search
  const {
    data: searchPage,
    isLoading: isSearching,
    isError: isSearchError,
    error: searchError
  } = useSearchFormTypes(effectiveFilters);

  const searchActive = !!effectiveFilters;

  // pick active page (rows + metadata)
  const activePage = searchActive ? (searchPage ?? {}) : (basePage ?? {});
  const rows = activePage.items ?? [];
  const currentPage = activePage.pageNumber ?? page;
  const totalPages =
    typeof activePage.totalPages === 'number'
      ? activePage.totalPages
      : (activePage.hasNext ? currentPage + 1 : currentPage);
  const hasPrevious = activePage.hasPrevious ?? currentPage > 1;
  const hasNext = activePage.hasNext ?? (rows.length >= pageSize);
  const loading = searchActive ? isSearching : isBaseLoading;

  // counts (no hook to avoid lint rule)
  const countLabel =
    typeof activePage?.totalCount === 'number' ? activePage.totalCount : rows.length;

  // early returns after hooks
  if (isBaseError && baseError?.code === 'ERR_NETWORK') return <NetworkError error={baseError} />;
  if (isBaseError) return <span>Oops something happened....</span>;
  if (isSearchError) console.warn("Form types search error:")//, searchError);

  // paging handlers
  const clearSearch = () => { setQuery(""); setPage(1); };
  const next = () => hasNext && setPage(p => p + 1);
  const prev = () => hasPrevious && setPage(p => Math.max(1, p - 1));
  const gotoPage = (p) => setPage(Math.max(1, p));
  const changePageSize = (sz) => { setPageSize(sz); setPage(1); };

  return (
    <div className="w-full flex flex-col space-y-5 px-10">
      <div className="branch_header">
        <div className="branch_header_title_group">
          <div className="branch_header_title_main">Forms</div>
          {!loading && (
            <div className="branch_header_title_sub">
              {countLabel === 0 ? 'No ' : countLabel} forms
            </div>
          )}
        </div>
        <div className="branch_header_buttons_group">
          <Link to={'add'} className="branch_header_buttons_right bg-[#0E86D4]">
            <FiPlusCircle style={{ marginRight: "10px" }} />
            New Form
          </Link>
        </div>
      </div>

      {/* Search */}
      <SearchBox
        placeholder="Search forms (name / description / category)"
        value={query}
        onChange={setQuery}
      />

      {/* Page size selector + clear */}
      <div className="flex items-center gap-2 -mt-2">
        <span className="text-sm text-gray-600">Page size:</span>
        <select
          className="border rounded px-2 py-1 text-sm"
          value={pageSize}
          onChange={(e) => changePageSize(Number(e.target.value))}
        >
          {[10, 20, 50, 100].map((sz) => (
            <option key={sz} value={sz}>{sz}</option>
          ))}
        </select>
        <button
          className="ml-auto text-sm underline text-blue-600"
          onClick={clearSearch}
          type="button"
          title="Clear search"
        >
          Clear search
        </button>
      </div>

      <PortalDataTable
        cols={formColumns}
        loading={loading}
        rows={rows}
        currentPage={currentPage}
        totalPages={totalPages}
        hasPrevious={hasPrevious}
        hasNext={hasNext}
        next={next}
        previous={prev}           
        onPageChange={gotoPage}
      />
    </div>
  );
};

export default Forms;
